import React, { useMemo, useState } from 'react';
import DataTable from '../../../../../../common/components/DataTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../template/store';
import ToggleDiv from '../../../../../../../components/common/ToggleDiv';
import { useLinerConfig } from '../../../../../../../hooks/useLinerConfig';
import useBlCountry from '../../../../../../../hooks/useBlCountry';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { encryptAndEncode } from '../../../../../../../encrypt-util';
import { openDocumentInNewTab } from '../../../../../../../helpers/openDocumentInNewTab';

interface ReleasePopTableProps {
  blCountry: string;
}

const ReleasePopTable = ({blCountry}: ReleasePopTableProps) => {
  const releasePOPData = useSelector((state: RootState) => state.invoiceUnpaid.releasePOPData);
  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const linerId = useSelector((state: RootState) => state.navbarTop.linerId);
  // const blCountry = useBlCountry('view', 'import', 820)[0];
  const linerConfig = useLinerConfig(blCountry, linerId, 820);
  const [openPopDocsRowId, setOpenPopDocsRowId] = useState<string | null>(null);

  const POPColumns = useMemo(
    () => [
      {
        accessor: 'invno',
        Header: 'Invoice Number',
        width: 200,
        maxWidth: 150,
      },
      {
        accessor: 'ccy',
        Header: 'CCY',
        width: 150,
        maxWidth: 100,
      },
      {
        accessor: 'amount',
        Header: 'Amount',
        width: 200,
        maxWidth: 150,
      },
      {
        accessor: 'invtype',
        Header: 'Invoice Type',
        width: 200,
        maxWidth: 150,
      },
      {
        accessor: 'invstatus',
        Header: 'Paid',
        width: 100,
        maxWidth: 80,
        Cell: (rowdata: any) => {
          const { invstatus, invoicestatus } = rowdata.row.original;
          return (
            <span>
              {invstatus && invstatus?.trim() != '' ? invstatus : invoicestatus == 1 ? 'Paid' : ''}
            </span>
          );
        },
      },
      {
        accessor: 'invref',
        Header: 'Payment Reference No',
        width: 250,
        maxWidth: 200,
      },
      {
        accessor: 'invatt',
        Header: 'Payment Proof',
        width: 200,
        maxWidth: 150,
        Cell: (rowdata: any) => {
          const row = rowdata.row.original;
          const activeDocs = Array.isArray(row.popAttachments)
            ? row.popAttachments.filter(
                (att: any) => att.active == '1'
              )
            : [];

          if (activeDocs.length == 0) {
            return null;
          }

					const openDoc = async (popAttId: string | number) => {
					  await openDocumentInNewTab({
					    url: '/api/edo-rest-v2/v2/common/openDocument',
					    params: {
					      id: encryptAndEncode(popAttId),
					      moduleTp: 'PAYMENT_PROOF_NEW',
					      linerId: encryptAndEncode(profileDetails?.partnerId),
					    },
					  });
					};

          if (activeDocs.length == 1) {
            const att = activeDocs[0];
            return (
              <span
                style={{ color: '#2c7be5', cursor: 'pointer' }}
                onClick={() => openDoc(att.popAttId)}
              >
                {att.fileName ?? ''}
              </span>
            );
          }

          const rowKey = String(rowdata.row.id);

          return (
            <OverlayTrigger
              show={openPopDocsRowId == rowKey}
              onToggle={(nextShow) =>
                setOpenPopDocsRowId(nextShow ? rowKey : null)
              }
              trigger="click"
              placement="bottom-end"
              rootClose
              container={document.body}
              popperConfig={{ strategy: 'fixed' }}
              overlay={
                <Popover
                  id={`pop-docs-${rowKey}`}
                  className="dropdown-menu-card shadow-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Popover.Body className="p-0 bg-white">
                    {activeDocs.map((att: any, index: number) => (
                      <div
                        key={att.popAttId ?? index}
                        className={`px-2 py-1${
                          index < activeDocs.length - 1 ? ' border-bottom' : ''
                        }`}
                      >
                        <span
                          className="text-primary cursor-pointer fs--1"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDoc(att.popAttId);
                          }}
                        >
                          {att.fileName ?? ''}
                        </span>
                      </div>
                    ))}
                  </Popover.Body>
                </Popover>
              }
            >
              <span
                style={{
                  color: '#2c7be5',
                  cursor: 'pointer',
                  fontWeight: openPopDocsRowId === rowKey ? 'bold' : 'normal',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                View all docs
              </span>
            </OverlayTrigger>
          );
        },
      },
    ],
    [profileDetails?.partnerId, openPopDocsRowId]
  );

  const shouldShowTable =
    profileDetails?.partnerType === 'liner' &&
    (linerConfig.crrUnpaidHardStop == 'Y' || linerConfig.crrUnpaidSoftStop == 'Y') &&
    releasePOPData.length > 0;

  if (!shouldShowTable) {
    return null;
  }

  const hasUnpaidInvoices = releasePOPData.findIndex((i) => i.invoicestatus != 1) != -1;

  return (
    <ToggleDiv
      className="col-lg-12 mb-2 demmurageTable"
      style={{ zIndex: 1 }}
      showText={'Click here to view Payment details'}
      hideText={'Click here to hide Payment details'}
      initial={hasUnpaidInvoices}
    >
      <DataTable
        data={releasePOPData}
        gridName="RELEASE_POP_DO_EXTENSION"
        columns={POPColumns}
        sortable={false}
        searchable={false}
        showColumnFilter={false}
        selection={false}
        perPage={10}
        pagination={false}
        showExport={false}
        uniqueFilterKey="releasepop"
        autoHeight={true}
        staticHiddenCols={[]}
        maxHeight="250px"
      />
    </ToggleDiv>
  );
};

export default ReleasePopTable;
